import { RollbackPanel } from "@/components/player-data/RollbackPanel";
import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  lookupUser,
  getPlayerDataSnapshot,
  updateUserData,
  getPlayerIdentities,
  getPlayerBans,
  getPlayerFlags,
  ApiError,
  type UnlinkProvider,
} from "@/lib/api";
import { formatTimestamp } from "@/lib/utils";
import { useDirtyStore } from "@/lib/dirty-store";
import { useOpenedPlayersStore } from "@/lib/opened-players-store";
import { playerSearchOrigin, playerSearchPath } from "@/lib/player-navigation";
import { TabPanel, type TabId } from "@/components/player-data/TabPanel";
import { AccountTab } from "@/components/player-data/AccountTab";
import { PurchasedIaps } from "@/components/player-data/PurchasedIaps";
import { readPurchasedIaps, purchaseRestoration } from "@/lib/purchase-restoration";
import { HeroesTab } from "@/components/player-data/HeroesTab";
import { InventoryTab } from "@/components/player-data/InventoryTab";
import { CostumesTab } from "@/components/player-data/CostumesTab";
import { MailTab } from "@/components/player-data/MailTab";
import { SideContentsTab } from "@/components/player-data/SideContentsTab";
import { RawTab, type RawKeyEntry } from "@/components/player-data/RawTab";
import { SendMailDialog } from "@/components/player-actions/SendMailDialog";
import { ManageBansDialog } from "@/components/player-actions/ManageBansDialog";
import { ResetPasswordDialog } from "@/components/player-actions/ResetPasswordDialog";
import { CopyDevAccountDialog } from "@/components/player-actions/CopyDevAccountDialog";
import { DeleteAccountDialog } from "@/components/player-actions/DeleteAccountDialog";
import { MigrateLegacyDialog } from "@/components/player-actions/MigrateLegacyDialog";
import { MoveLoginDialog } from "@/components/player-actions/MoveLoginDialog";
import {MigrationEmailDialog} from '@/components/player-actions/MigrationEmailDialog';
import { UnlinkIdentityDialog } from "@/components/player-actions/UnlinkIdentityDialog";
import { FlagDetails } from "@/components/FlagDetails";
import type { CloudAllData } from "@/types/cloud-data";

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm border-b border-border/50 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-mono text-xs">{value ?? "—"}</span>
    </div>
  );
}

export function PlayerDetailRoute() {
  const { playfabId } = useParams<{ playfabId: string }>();
  return <PlayerDetail key={playfabId} />;
}

function PlayerDetail() {
  const { playfabId } = useParams<{ playfabId: string }>();
  const nav = useNavigate();
  const location = useLocation();
  const origin = playerSearchOrigin(location.state);
  const confirmLeave = useDirtyStore((s) => s.confirmLeave);
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabId>("account");
  const [unlinkProvider, setUnlinkProvider] = useState<UnlinkProvider | null>(null);
  const [migrationNotice, setMigrationNotice] = useState<string | null>(null);
  const requestedAction = new URLSearchParams(location.search).get("action");
  const [action, setAction] = useState<"mail" | "restore" | "rollback" | "bans" | "password" | "delete" | "migrate" | "migration-email" | "move-google" | "copy-dev" | null>(
    requestedAction === "mail" || requestedAction === "bans" ? requestedAction : null
  );

  useEffect(() => {
    if (requestedAction === "mail" || requestedAction === "bans") setAction(requestedAction);
  }, [requestedAction]);

  function closeAction() {
    setAction(null);
    if (new URLSearchParams(location.search).has("action")) {
      const params = new URLSearchParams(location.search);
      params.delete("action");
      nav({ pathname: location.pathname, search: params.toString() ? `?${params}` : "" }, { replace: true, state: location.state });
    }
  }

  // Sparse draft: only modified UserData keys. Empty == clean.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [draftVersion, setDraftVersion] = useState<number | null>(null);

  const setDirty = useDirtyStore((s) => s.setDirty);
  const upsertOpened = useOpenedPlayersStore((s) => s.upsert);

  const account = useQuery({
    queryKey: ["pf-account", playfabId],
    queryFn: () => lookupUser("playfabId", playfabId!),
    enabled: !!playfabId,
  });

  const userData = useQuery({
    queryKey: ["pf-userdata", playfabId],
    queryFn: () => getPlayerDataSnapshot(playfabId!),
    enabled: !!playfabId,
    refetchOnWindowFocus: Object.keys(draft).length === 0,
    refetchInterval: Object.keys(draft).length === 0 ? 15_000 : false,
  });

  const identities = useQuery({
    queryKey: ["player-identities", playfabId],
    queryFn: () => getPlayerIdentities(playfabId!),
    enabled: !!playfabId,
  });

  const d1Bans = useQuery({
    queryKey: ["player-bans", playfabId],
    queryFn: () => getPlayerBans(playfabId!),
    enabled: !!playfabId,
  });

  const flags = useQuery({
    queryKey: ["player-flags", playfabId],
    queryFn: async () => {
      try { return await getPlayerFlags(playfabId!); }
      catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: !!playfabId,
  });

  // Server-side data, flattened.
  const dataMap: Record<string, string> = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(userData.data?.Data ?? {})) {
      out[k] = v.Value;
    }
    return out;
  }, [userData.data]);

  // What the UI renders from: draft layered on top of server.
  const effective: Record<string, string> = useMemo(() => {
    return { ...dataMap, ...draft };
  }, [dataMap, draft]);

  const dirty = Object.keys(draft).length > 0;
  const dirtyCount = Object.keys(draft).length;
  const noSaveYet = userData.data?.source === "cloudflare" && userData.data.saveVersion === null;

  // Push dirty state into the global store for sidebar / unload guards.
  useEffect(() => {
    setDirty(dirty);
    return () => setDirty(false);
  }, [dirty, setDirty]);

  // Browser-level guard while dirty.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // Add this player to the session's opened-players list as soon as we know
  // a friendly label (display name → username → short ID).
  useEffect(() => {
    if (!playfabId) return;
    const info = account.data;
    const label =
      info?.TitleInfo?.DisplayName ||
      info?.Username ||
      `${playfabId.slice(0, 8)}…`;
    upsertOpened({ playfabId, label, origin });
  }, [playfabId, account.data, origin, upsertOpened]);

  const rawKeys: RawKeyEntry[] = useMemo(() => {
    return Object.entries(userData.data?.Data ?? {})
      .map(([key, v]) => ({ key, permission: v.Permission, lastUpdated: v.LastUpdated }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [userData.data]);

  const cloudData: CloudAllData | null = useMemo(() => {
    const raw = effective["EC2_CLOUD_ALL_DATA"];
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [effective]);

  const heroes = cloudData?.heroesData?.heroes ?? [];

  const purchaseHistory: string[] = useMemo(() => {
    return readPurchasedIaps(cloudData, effective["EC2_SUS"]);
  }, [cloudData, effective]);

  // Draft helpers — all child tabs ultimately route through these.
  function setDraftKey(key: string, value: string) {
    if (noSaveYet) return;
    if (!dirty) setDraftVersion(userData.data?.saveVersion ?? null);
    setDraft((prev) => {
      // Compare semantically: pretty-printing a JSON value isn't a real change.
      // Falls back to string equality for non-JSON keys.
      const server = dataMap[key];
      const isClean = (() => {
        if (server === value) return true;
        if (server === undefined) return false;
        try {
          return JSON.stringify(JSON.parse(server)) === JSON.stringify(JSON.parse(value));
        } catch {
          return false;
        }
      })();
      if (isClean) {
        const { [key]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });
  }

  function setDraftCloud(next: CloudAllData) {
    setDraftKey("EC2_CLOUD_ALL_DATA", JSON.stringify(next));
  }

  function discard() {
    if (!dirty) return;
    if (!window.confirm(`Discard ${dirtyCount} unsaved change${dirtyCount === 1 ? "" : "s"}?`)) return;
    setDraft({});
  }

  const targetLabel =
    account.data?.TitleInfo?.DisplayName ||
    account.data?.Username ||
    null;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, string> = { ...draft, FORCE: "1" };
      return updateUserData(playfabId!, payload, { targetLabel, expectedSaveVersion: draftVersion });
    },
    onSuccess: () => {
      setDraft({});
      qc.invalidateQueries({ queryKey: ["pf-userdata", playfabId] });
    },
  });

  const info = account.data;
  const loading = account.isLoading || userData.isLoading;
  const target = info ? { id: info.PlayFabId, displayName: targetLabel } : null;

  return (
    <AppShell
      title="Player"
      subtitle={
        info?.TitleInfo?.DisplayName
          ? `${info.TitleInfo.DisplayName} · ${playfabId}`
          : playfabId
      }
      onBack={() => { if (confirmLeave()) nav(playerSearchPath(origin)); }}
    >
      <div className="space-y-4 max-w-6xl">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{noSaveYet ? "No save yet" : userData.data?.source === "cloudflare"
            ? `Cloud save · version ${userData.data.saveVersion}`
            : userData.data ? "Legacy PlayFab data" : "Loading player data…"}</span>
          <Button variant="secondary" size="sm" disabled={dirty || userData.isFetching}
            onClick={() => { void userData.refetch(); }}>
            {userData.isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
        {/* Sticky save bar — only when there are unsaved changes */}
        {dirty && (
          <div className="sticky top-0 z-20 -mx-6 -mt-5 px-6 py-3 border-b border-amber-500/40 bg-amber-500/10 backdrop-blur flex items-center justify-between">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-amber-300 font-medium">
                {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
              </span>
              <span className="text-xs text-muted-foreground font-mono">
                {Object.keys(draft).join(", ")}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={discard}
                disabled={saveMutation.isPending}
              >
                Discard
              </Button>
              <Button
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        )}

        {info && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Identity</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-x-4">
                <FieldRow label="UID" value={info.PlayFabId} />
                <FieldRow label="Username" value={info.Username ?? "—"} />
                <FieldRow label="Display" value={info.TitleInfo?.DisplayName ?? "—"} />
                <FieldRow label="Email" value={info.PrivateInfo?.Email ?? "—"} />
                <FieldRow label="Account created" value={formatTimestamp(info.Created)} />
                {info.TitleInfo?.Created && info.TitleInfo.Created !== info.Created && (
                  <FieldRow label="Joined EC2" value={formatTimestamp(info.TitleInfo.Created)} />
                )}
                {info.BackendFirstSeen && (
                  <FieldRow label="First seen on server" value={formatTimestamp(info.BackendFirstSeen)} />
                )}
                <FieldRow label={userData.data?.source === "cloudflare" ? "Last session activity" : "Last Login"} value={formatTimestamp(info.TitleInfo?.LastLogin)} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Quick actions</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                <Button variant="secondary" size="sm" disabled={dirty || saveMutation.isPending || userData.data?.source !== "cloudflare"} onClick={() => setAction(action === "rollback" ? null : "rollback")}>Rollback</Button>
                <Button variant="secondary" size="sm" onClick={() => setAction("copy-dev")}
                  disabled={dirty || saveMutation.isPending || userData.isFetching || userData.isError || userData.data?.source !== "cloudflare"}>
                  Copy as dev account
                </Button>
                {userData.data?.source === "playfab" && <p className="text-xs text-muted-foreground">Migrate this account before making a dev copy.</p>}
                {!/^EC2X/i.test(info.PlayFabId) && userData.data?.source !== "cloudflare" && (
                  <Button variant="secondary" size="sm" disabled={dirty || saveMutation.isPending} onClick={() => setAction("migrate")}>
                    Migrate legacy account
                  </Button>
                )}
                {userData.data?.source==='cloudflare'&&<Button variant="secondary" size="sm" onClick={()=>setAction('migration-email')}>set linked email and password</Button>}
                {userData.data?.source==='cloudflare'&&<Button variant="secondary" size="sm" disabled={dirty||saveMutation.isPending} onClick={()=>setAction('move-google')}>Move Google login</Button>}
                <Button variant="secondary" size="sm" onClick={() => setAction("mail")}>
                  Send mail
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setAction("bans")}>
                  Manage bans
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setAction("delete")}>
                  Delete account permanently
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setAction("password")}
                  disabled={!identities.data?.some((row) => row.provider === "password")}>
                  Reset password
                </Button>
                {identities.data && !identities.data.some((row) => row.provider === "password") && (
                  <p className="text-xs text-muted-foreground">Password reset requires a backend email/password identity.</p>
                )}
                <Button variant="secondary" size="sm" onClick={() => nav(`/audit?playfabId=${info.PlayFabId}`)}>
                  Audit history
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Deliberately NOT gated on `info` (the PlayFab lookup). An account
            minted after the auth cutover has no PlayFab record at all, so
            `info` is empty for exactly the accounts these two cards exist to
            describe — gating them here made them invisible from the Accounts
            search. Both queries below run off playfabId alone. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card>
              <CardHeader>
                <CardTitle>Identities (our auth)</CardTitle>
              </CardHeader>
              <CardContent>
                {identities.isLoading ? (
                  <Skeleton className="h-16 w-full" />
                ) : identities.isError ? (
                  <span className="text-sm text-destructive">Could not load login methods: {String(identities.error)}</span>
                ) : identities.data && identities.data.length > 0 ? (
                  <div className="space-y-1">
                    {identities.data.map((row) => (
                      <div
                        key={row.provider}
                        className="flex items-center justify-between py-1.5 text-sm border-b border-border/50 last:border-0"
                      >
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{row.provider === "password" ? "email/password" : row.provider}</Badge>
                          <span className="text-xs text-muted-foreground font-mono">
                            {row.email ?? row.display_name ?? row.subject}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground font-mono">
                            linked {formatTimestamp(row.linked_at)}
                          </span>
                          {["google", "discord", "apple", "password"].includes(row.provider) && (
                            <Button variant="destructive" size="sm"
                              aria-label={`Unlink ${row.provider === "password" ? "email/password" : row.provider}`}
                              disabled={identities.isFetching || !identities.data.some((other) => other.provider !== row.provider)}
                              title={identities.data.some((other) => other.provider !== row.provider)
                                ? "Unlink this login method" : "Cannot unlink the only remaining login method"}
                              onClick={() => setUnlinkProvider(row.provider as UnlinkProvider)}>Unlink</Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {new Set(identities.data.map((row) => row.provider)).size === 1 && (
                      <p className="text-xs text-muted-foreground">Cannot unlink the only remaining login method. Connect another method first.</p>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    No identities in the new auth system — this account has not
                    logged in through our own tokens yet.
                  </span>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Bans (our backend)</CardTitle>
              </CardHeader>
              <CardContent>
                {d1Bans.isLoading ? (
                  <Skeleton className="h-16 w-full" />
                ) : d1Bans.isError ? (
                  <span className="text-sm text-destructive">Could not load backend bans: {String(d1Bans.error)}</span>
                ) : d1Bans.data && d1Bans.data.bans.length > 0 ? (
                  <div className="space-y-1">
                    {d1Bans.data.bans.map((row) => {
                      const active =
                        row.revoked_at === null &&
                        (row.expires_at === null || row.expires_at > Date.now());
                      return (
                        <div
                          key={row.id}
                          className="flex items-center justify-between py-1.5 text-sm border-b border-border/50 last:border-0"
                        >
                          <div className="flex items-center gap-2">
                            <Badge variant={active ? "destructive" : "secondary"}>
                              {active ? "active" : "inactive"}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {row.reason ?? "no reason given"}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground font-mono">
                            {formatTimestamp(row.created_at)}
                            {row.expires_at ? ` → ${formatTimestamp(row.expires_at)}` : " (permanent)"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    No backend bans recorded for this account.
                  </span>
                )}
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center gap-2 p-4 text-sm font-semibold rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <span aria-hidden="true" className="transition-transform group-open:rotate-90">▶</span>
                  Flags and retained evidence
                  {flags.data && <span className="ml-auto text-xs font-normal text-muted-foreground">{flags.data.summary.total_count.toLocaleString()} occurrences</span>}
                </summary>
              <CardContent className="space-y-3 text-xs">
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" disabled={flags.isFetching} onClick={() => void flags.refetch()}>
                    {flags.isFetching ? "Loading…" : "Refresh flags"}
                  </Button>
                </div>
                {flags.isPending ? <Skeleton className="h-16 w-full" /> : flags.error ? (
                  <Alert variant="destructive"><AlertDescription>Could not load flags: {String(flags.error)}</AlertDescription></Alert>
                ) : flags.data ? <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={flags.data.summary.reviewed ? "success" : "destructive"}>
                      {flags.data.summary.reviewed ? "Reviewed" : "Unreviewed"}
                    </Badge>
                    <span className="text-muted-foreground">{flags.data.summary.total_count.toLocaleString()} total occurrences</span>
                    <span className="text-muted-foreground">First flagged {formatTimestamp(flags.data.summary.first_ts)}</span>
                    <span className="text-muted-foreground">Last flagged {formatTimestamp(flags.data.summary.last_ts)}</span>
                  </div>
                  <FlagDetails rows={flags.data.details} />
                </> : <p className="text-sm text-muted-foreground">No flags recorded for this account.</p>}
              </CardContent>
              </details>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Auth failure lockout (Layer 2, migration 013)</CardTitle>
              </CardHeader>
              <CardContent>
                {d1Bans.isLoading ? (
                  <Skeleton className="h-16 w-full" />
                ) : d1Bans.isError ? (
                  <span className="text-sm text-destructive">Could not load auth failures: {String(d1Bans.error)}</span>
                ) : d1Bans.data && d1Bans.data.authFailures.length > 0 ? (
                  <div className="space-y-1">
                    {d1Bans.data.authFailures.map((row) => {
                      const locked = row.locked_until !== null && row.locked_until > Date.now();
                      return (
                        <div
                          key={row.kind}
                          className="flex items-center justify-between py-1.5 text-sm border-b border-border/50 last:border-0"
                        >
                          <div className="flex items-center gap-2">
                            <Badge variant={locked ? "destructive" : "secondary"}>
                              {locked ? "locked" : "not locked"}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {row.kind}: {row.failures} failure{row.failures === 1 ? "" : "s"} since{" "}
                              {formatTimestamp(row.first_at)}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground font-mono">
                            {row.locked_until ? `locked until ${formatTimestamp(row.locked_until)}` : "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    No recent password-verification failures for this account
                    (auth_failure). This is what "why can't I log in" answers
                    when it isn't a ban.
                  </span>
                )}
              </CardContent>
            </Card>
        </div>

        {migrationNotice && <Alert><AlertDescription>{migrationNotice}</AlertDescription></Alert>}
        {target && action === "migrate" && <MigrateLegacyDialog key={`migrate:${target.id}`} target={target} onClose={closeAction}
          onMigrated={message => { setMigrationNotice(message); closeAction(); }} />}
        {target&&action==='migration-email'&&<MigrationEmailDialog target={target} onClose={closeAction}
          initialEmail={identities.data?.find(i=>i.provider==='password')?.subject??identities.data?.find(i=>i.provider==='playfab')?.email??''}/>}
        {target&&action==='move-google'&&<MoveLoginDialog provider="google" target={target} onClose={closeAction} onMoved={message=>{setMigrationNotice(message);closeAction();}}/>}
        {target && action === "rollback" && !dirty && <Card><CardContent className="pt-4"><RollbackPanel target={target} /></CardContent></Card>}
        {!noSaveYet && <TabPanel activeTab={tab} onTabChange={setTab} />}

        {userData.isError ? (
          <Alert variant="destructive">
            <AlertDescription>Could not load current player data: {userData.error.message}. Use Refresh to retry.</AlertDescription>
          </Alert>
        ) : loading ? (
          <Skeleton className="h-64 w-full" />
        ) : noSaveYet ? (
          <Alert><AlertDescription>No save yet. Identity and account actions are available; game data will appear after the player's first save.</AlertDescription></Alert>
        ) : tab === "account" ? (
          cloudData ? (
            <AccountTab
              cloudData={cloudData}
              onSaveCloud={setDraftCloud}
            />
          ) : (
            <Alert>
              <AlertDescription>
                No <code>EC2_CLOUD_ALL_DATA</code> on this player. Use the Raw tab to inspect what is stored.
              </AlertDescription>
            </Alert>
          )
        ) : tab === "heroes" ? (
          <HeroesTab heroes={heroes} onEditHero={() => {}} />
        ) : tab === "inventory" ? (
          <InventoryTab cloudData={cloudData} onSaveCloudData={setDraftCloud} />
        ) : tab === "costumes" ? (
          <CostumesTab cloudData={cloudData} onSaveCloudData={setDraftCloud} disabled={saveMutation.isPending} />
        ) : tab === "mail" ? (
          <MailTab playfabId={playfabId!} onSendMail={() => setAction("mail")} />
        ) : tab === "sidecontents" ? (
          <SideContentsTab data={effective} onSaveKey={setDraftKey} />
        ) : tab === "raw" ? (
          <RawTab keys={rawKeys} data={effective} onSave={setDraftKey} />
        ) : null}

        {!loading && !userData.isError && !noSaveYet && <PurchasedIaps
          ids={purchaseHistory} canRestore={!!target && !dirty}
          onRestore={() => setAction("restore")} />}
        {dirty && purchaseHistory.length > 0 && <p className="text-xs text-muted-foreground">Save or discard player data changes before restoring purchases.</p>}

        {saveMutation.error && (
          <Alert variant="destructive">
            <AlertDescription>{saveMutation.error.message === "save_conflict"
              ? "The player has a newer save. Discard your draft, refresh, and reapply your changes."
              : String(saveMutation.error)}</AlertDescription>
          </Alert>
        )}
        {saveMutation.isSuccess && !dirty && (
          <Alert>
            <AlertDescription>Saved.</AlertDescription>
          </Alert>
        )}

        {!info && account.isError && <Alert variant="destructive"><AlertDescription>
          Could not resolve this account: {String(account.error)}. <Button variant="outline" size="sm" onClick={() => nav(playerSearchPath(origin))}>Look up a player</Button>
        </AlertDescription></Alert>}
        {target && action === "mail" && <SendMailDialog key={`mail:${target.id}`} target={target} onClose={closeAction} />}
        {target && action === "restore" && <SendMailDialog key={`restore:${target.id}`} target={target}
          initialSubject="Restore purchases" initialRewards={purchaseRestoration(purchaseHistory).rewards} onClose={closeAction} />}
        {target && action === "bans" && <ManageBansDialog key={`bans:${target.id}`} target={target} onClose={closeAction} />}
        {target && action === "password" && <ResetPasswordDialog key={`password:${target.id}`} target={target} onClose={closeAction} />}
        {target && action === "copy-dev" && <CopyDevAccountDialog key={`copy-dev:${target.id}`} target={target}
          saveVersion={userData.data?.saveVersion ?? 0} onClose={closeAction} />}
        {target && action === "delete" && <DeleteAccountDialog key={`delete:${target.id}`} target={target}
          onClose={closeAction} onDeleted={() => {
            setDraft({}); setDirty(false);
            useOpenedPlayersStore.getState().remove(target.id);
            qc.removeQueries({ predicate: query => query.queryKey.includes(target.id) });
            void qc.invalidateQueries();
            nav(playerSearchPath(origin));
          }} />}
        {target && unlinkProvider && <UnlinkIdentityDialog key={`unlink:${target.id}:${unlinkProvider}`}
          target={target} provider={unlinkProvider} onClose={() => setUnlinkProvider(null)} />}
      </div>
    </AppShell>
  );
}
